export interface AiAgentTemplate {
  id: string;
  name?: string;
  description?: string;
  category?: string;
  tier?: string;
  voiceId?: string;
  voiceProvider?: string;
  orchestrator?: string;
  llmProvider?: string;
  defaultGreeting?: string;
  /** Default closing message agents on this template inherit (org users may override per agent). */
  defaultClosingMessage?: string;
  tools?: Record<string, any>;
  conversationTree?: Record<string, any>;
  /** llmModel and aiDisclosureEnabled are stored inside defaultConfig */
  defaultConfig?: Record<string, any> | null;
  /** Outcomes seeded onto agents acquired from this template (set_outcome enum / workflow routing). */
  defaultDeclaredOutcomes?: string[];
  defaultSkills?: string[] | null;
  /** Admin-only: id of the shared-library prompt version this template defaults to. */
  defaultPromptVersionId?: string | null;
  /** Admin-only: resolved display name of that version ("Base Agent Prompt · v2 · tightened intro"). */
  defaultPromptVersionLabel?: string | null;
  demoAudioUrl?: string;
  iconUrl?: string;
  status?: string;
  version?: number;
  createdAt?: string;
}

export interface AiListingMedia {
  id: string;
  name?: string;
  url?: string;
  contentType?: string;
}

export type AiListingVisibility = "PUBLIC" | "PRIVATE";

export type AiAgentLifecycle = "KEPT" | "REMOVAL_SCHEDULED" | "REMOVED";

export interface AiListingOrganisation {
  id: string;
  name?: string | null;
}

export interface AiListingCharge {
  listingId: string;
  displayName: string;
  monthlyCostCents: number;
  quantity: number;
}

export interface AiAgentListing {
  id: string;
  template?: AiAgentTemplate;
  displayName?: string | null;
  longDescription?: string;
  perks?: string[] | null;
  limits?: string[] | null;
  media?: AiListingMedia | null;
  monthlyFee: number;
  discountPercent: number;
  perMinuteFee: number;
  visibility?: AiListingVisibility;
  allowedOrganisationSummaries?: AiListingOrganisation[];
  allowedOrganisationIds?: string[];
  effectiveMonthlyFee?: number | null;
  effectiveOutboundMonthlyFee?: number | null;
  effectivePerMinuteFee?: number | null;
  featured: boolean;
  sortOrder: number;
  acquisitionCount: number;
  publishedAt?: string;
  unlistedAt?: string | null;
  createdAt?: string;
}

export const AI_LLM_PROVIDERS = [
  { value: "OPENAI", label: "OpenAI" },
  { value: "GROQ", label: "Groq" },
  { value: "ANTHROPIC", label: "Anthropic (Claude)" },
];

export const AI_AGENT_TIERS = [
  { value: "BASIC", label: "Basic" },
  { value: "MEDIUM", label: "Medium" },
  { value: "PREMIUM", label: "Premium" },
];

/**
 * Only add models Vapi actually supports. Vapi rejects an unsupported model with HTTP 400 and the
 * whole call dies before any audio — and `modelMatchesProvider` won't catch it, since that only
 * checks the model looks like the right *provider's* (any "gpt-" prefix passes). As of Aug 2026 Vapi
 * supports the GPT-5 family below plus GPT-4.1 / 4o; notably NOT gpt-5.5 or the gpt-5.6-* line.
 *
 * Cost notes are input-rate multiples of gpt-4o-mini, the current default. Latency on live calls is
 * NOT characterised for the GPT-5 entries — test on a real call before rolling one out widely.
 */
export const LLM_MODELS_OPENAI = [
  { value: "gpt-4o-mini", label: "GPT-4o mini", description: "Fast · cheap · great for most calls" },
  { value: "gpt-4o", label: "GPT-4o", description: "Smarter · slower · more capable" },
  { value: "gpt-4.1-nano", label: "GPT-4.1 nano", description: "Fastest · cheapest · 4.1 gen" },
  { value: "gpt-4.1-mini", label: "GPT-4.1 mini", description: "Fast · cheap · 4.1 gen" },
  { value: "gpt-4.1", label: "GPT-4.1", description: "Capable · slower · 13× 4o mini cost" },
  { value: "gpt-5-nano", label: "GPT-5 nano", description: "Cheapest overall · weakest at complex rules" },
  { value: "gpt-5-mini", label: "GPT-5 mini", description: "Cheap · better rule-following · 1.7× 4o mini" },
  { value: "gpt-5.4-mini", label: "GPT-5.4 mini", description: "Best balance · 5× 4o mini cost" },
  { value: "gpt-5.4", label: "GPT-5.4", description: "Most capable · 17× 4o mini cost" },
];

export const LLM_MODELS_GROQ = [
  { value: "llama-3.1-8b-instant", label: "Llama 3.1 8B Instant", description: "Fastest · lowest latency · best for live calls" },
  { value: "llama-3.3-70b-versatile", label: "Llama 3.3 70B", description: "Best quality on Groq · slower" },
  { value: "gemma2-9b-it", label: "Gemma 2 9B", description: "Fast · Google model" },
];

export const LLM_MODELS_ANTHROPIC = [
  { value: "claude-haiku-4-5", label: "Claude Haiku 4.5", description: "Fastest · cheapest · best for live calls" },
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6", description: "Smarter · slower · more capable" },
];

// Deepgram speech-to-text models (Native orchestrator). Null = platform default.
export const STT_MODELS_DEEPGRAM = [
  { value: "nova-3", label: "Nova 3", description: "Latest · best accuracy on accents & phone audio" },
  { value: "nova-2-phonecall", label: "Nova 2 Phone Call", description: "Telephony-tuned for 8kHz lines" },
  { value: "nova-2", label: "Nova 2", description: "Previous generation" },
];

// Speech-to-text providers for the Native orchestrator (admin-only; orgs never see this).
export const AI_STT_PROVIDERS = [
  { value: "DEEPGRAM", label: "Deepgram" },
  { value: "OPENAI", label: "OpenAI" },
  { value: "CARTESIA", label: "Cartesia" },
];

export const STT_MODELS_OPENAI = [
  { value: "gpt-4o-mini-transcribe", label: "GPT-4o mini Transcribe", description: "Fast · cheap · realtime streaming" },
  { value: "gpt-4o-transcribe", label: "GPT-4o Transcribe", description: "Best accuracy · slower" },
  { value: "whisper-1", label: "Whisper", description: "Classic Whisper · widest language coverage" },
];

export const STT_MODELS_CARTESIA = [
  { value: "ink-whisper", label: "Ink-Whisper", description: "Telephony-tuned streaming STT" },
];

export function getSttModelsForProvider(provider: string | null | undefined) {
  switch ((provider || "DEEPGRAM").toUpperCase()) {
    case "OPENAI": return STT_MODELS_OPENAI;
    case "CARTESIA": return STT_MODELS_CARTESIA;
    default: return STT_MODELS_DEEPGRAM;
  }
}

// OpenAI Realtime voices (OPENAI_REALTIME orchestrator). Null = platform default.
export const REALTIME_VOICES_OPENAI = [
  { value: "marin", label: "Marin", description: "Newest · natural" },
  { value: "cedar", label: "Cedar", description: "Newest · natural" },
  { value: "alloy", label: "Alloy", description: "Neutral" },
  { value: "ash", label: "Ash", description: "Warm" },
  { value: "ballad", label: "Ballad", description: "Expressive" },
  { value: "coral", label: "Coral", description: "Bright" },
  { value: "echo", label: "Echo", description: "Crisp" },
  { value: "sage", label: "Sage", description: "Calm" },
  { value: "shimmer", label: "Shimmer", description: "Soft" },
  { value: "verse", label: "Verse", description: "Energetic" },
];

export function getLlmModelsForProvider(provider: string | null | undefined) {
  switch (provider?.toUpperCase()) {
    case "OPENAI": return LLM_MODELS_OPENAI;
    case "GROQ": return LLM_MODELS_GROQ;
    case "ANTHROPIC": return LLM_MODELS_ANTHROPIC;
    default: return [];
  }
}

/**
 * True when a curated model is no longer offered by its provider (deprecated). `availability` is the
 * live per-provider model-id map from the backend. An empty/missing provider list means "unknown"
 * (provider unreachable or no key) — we never flag in that case to avoid false positives.
 */
export function isLlmModelDeprecated(
  provider: string | null | undefined,
  modelId: string,
  availability: Record<string, string[]> | undefined,
): boolean {
  const live = availability?.[(provider || "").toUpperCase()];
  if (!live || live.length === 0) return false;
  return !live.includes(modelId);
}

export interface AiAgent {
  id: string;
  organisation?: any;
  template?: AiAgentTemplate;
  name: string;
  voiceId?: string;
  /** Admin-only (omitted from org responses; present only on platform-admin endpoints). */
  orchestrator: string;
  /** Neutral flag exposed to org users instead of the orchestrator name. */
  isRealtime?: boolean;
  voiceProvider?: string;
  greeting?: string;
  /** Org-editable: how the agent should wrap up a normally-ending call. */
  closingMessage?: string;
  tools?: Record<string, any>;
  customConfig?: Record<string, any>;
  declaredOutcomes?: string[];
  /** Admin-only: ordered ids of SKILL-kind library prompts appended to this agent's system prompt. */
  skills?: string[];
  status: string;
  lifecycle?: AiAgentLifecycle;
  paidThrough?: string | null;
  tier?: string;
  llmProvider?: string;
  llmModel?: string;
  /** Admin-only: agent granted for free by a platform admin — excluded from billing. */
  complimentary?: boolean;
  /** Admin-only: id of the assigned prompt-library version (null → inherits template default). */
  promptVersionId?: string | null;
  /** Admin-only: resolved display name of that version ("Base Agent Prompt · v2 · tightened intro"). */
  promptVersionLabel?: string | null;
  /** aiDisclosureEnabled is stored inside customConfig */
  /** Resolved values (agent override ?? template default) — used to pre-fill the org editor. */
  effectiveSystemPrompt?: string;
  effectiveGreeting?: string;
  effectiveClosingMessage?: string;
  effectiveTools?: Record<string, any>;
  effectiveDeclaredOutcomes?: string[];
  effectiveSkills?: string[];
  defaultGreeting?: string;
  defaultClosingMessage?: string;
  assignedPhoneNumbers?: { id: string; number: string; alias?: string }[];
  createdAt?: string;
}

export type CallSentiment = "INTERESTED" | "NEUTRAL" | "NOT_INTERESTED";

export interface CallSummary {
  callSummary: string;
  outcome: string;
  keyPoints: string[];
  nextSteps: string[];
  sentiment: CallSentiment;
}

/**
 * One squad member's contribution to a Vapi call. `takeoverMs` is the silence the caller sat
 * through after the handoff before this member spoke; `stalled` means they gave up waiting and
 * spoke first.
 */
export interface SquadMemberStat {
  name: string;
  turns: number;
  tools: string[];
  takeoverMs?: number;
  stalled?: boolean;
}

export interface AiAgentCall {
  id: string;
  agent?: AiAgent;
  call?: { id?: string; direction?: string; phoneNumber?: string };
  contact?: { id?: string; firstName?: string; lastName?: string; company?: string };
  durationSeconds: number;
  cost?: number;
  transcript?: string;
  /** Raw JSON string of CallSummary — parse with parseCallSummary() */
  summary?: string;
  /** Top-level sentiment extracted from summary — available immediately after summarization */
  sentiment?: CallSentiment;
  /**
   * What the agent gathered from the caller (motivation, timeline, notes…), keyed by field name.
   * Only returned by the single-call endpoint — the paged list omits it.
   */
  collectedData?: Record<string, string>;
  /** How the call ran, per orchestrator. Vapi records its squad shape here; other orchestrators
   *  send nothing. Separate from collectedData, which is what the agent learned about the caller. */
  orchestratorDiagnostics?: {
    members?: SquadMemberStat[];
    handoffs?: number;
    stalledHandoffs?: number;
    /** A member that was handed off to and never spoke at all. */
    neverSpoke?: string;
  };
  /** Display name of the team member who took the call over from the AI, if anyone did. */
  escalatedToName?: string;
  escalatedAt?: string;
  outcome: string;
  startedAt?: string;
  endedAt?: string;
  createdAt?: string;
}

/**
 * An AI call happening right now. Comes from the backend's in-memory session registry rather than
 * the AiAgentCall table, which is only written once a call ends.
 */
export interface LiveAiCall {
  callId: string;
  callSid: string;
  agentId: string;
  agentName?: string | null;
  direction: string;
  phoneNumber?: string | null;
  contactName?: string | null;
  startedAt: string;
  /** Line count only — the transcript itself is fetched on demand when a call is opened. */
  transcriptLineCount: number;
}

export interface TranscriptLine {
  role: string;
  text: string;
  timestamp: string;
}

export interface AiSentimentLiveEvent {
  type: "ai_sentiment";
  callId: string;
  callSid: string;
  sentiment: CallSentiment;
  confidence: number;
  signal: string;
  timestamp: string;
}

export function parseCallSummary(raw: string | null | undefined): CallSummary | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.sentiment) {
      return parsed as CallSummary;
    }
    return null;
  } catch {
    return null;
  }
}

export interface VoiceModelOption {
  id: string;
  name: string;
  description?: string;
}

export interface VoiceOption {
  id: string;
  name: string;
  category?: string;
  previewUrl?: string;
  gender?: string;
  accent?: string;
  /** Short descriptor shown under the voice name (ElevenLabs description / Deepgram aura tags). */
  description?: string;
  /** Which TTS provider this voice belongs to (AiVoiceProvider value). Defaults to ElevenLabs. */
  provider?: string;
}

export interface VoiceModelsCatalogue {
  stt: VoiceModelOption[];
  llm: VoiceModelOption[];
  tts: VoiceModelOption[];
  voices: VoiceOption[];
}

export const AI_AGENT_CATEGORIES = [
  { value: "LEAD_QUALIFICATION", label: "Lead Qualification" },
  { value: "APPOINTMENT_SETTING", label: "Appointment Setting" },
  { value: "PROPERTY_INQUIRY", label: "Property Inquiry" },
  { value: "GENERAL_ASSISTANT", label: "General Assistant" },
  { value: "CUSTOM", label: "Custom" },
];

export const AI_VOICE_ORCHESTRATORS = [
  { value: "VAPI", label: "Vapi" },
  { value: "ELEVENLABS_CONVAI", label: "ElevenLabs Conversational AI" },
  { value: "NATIVE", label: "Retreevo (Native)" },
  { value: "OPENAI_REALTIME", label: "OpenAI Realtime (speech-to-speech)" },
];

// Voice providers shown in the UI. We start with ElevenLabs plus the two cheapest/most performant
// options (Cartesia — low-latency Sonic; Deepgram Aura — lowest cost and shares our Deepgram billing).
// The rest are hidden for now (kept here, commented, so they're trivial to re-enable once their
// backend TTS implementations land — today only ELEVENLABS is wired; others fall back to ElevenLabs).
export const AI_VOICE_PROVIDERS = [
  { value: "ELEVENLABS", label: "ElevenLabs" },
  { value: "CARTESIA", label: "Cartesia" },
  { value: "DEEPGRAM_AURA", label: "Deepgram Aura" },
  // { value: "OPENAI", label: "OpenAI TTS" },
  // { value: "PLAYHT", label: "PlayHT" },
  // { value: "AZURE", label: "Azure Speech" },
];

export const ARCHIVED_TEMPLATE_STATUS = "DEPRECATED";

export const AI_TEMPLATE_STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "PUBLISHED", label: "Published" },
  { value: ARCHIVED_TEMPLATE_STATUS, label: "Archived" },
];

export const AI_TEMPLATE_STATUS_FILTERS = [
  { value: "ACTIVE", label: "Active" },
  { value: "PUBLISHED", label: "Published" },
  { value: "DRAFT", label: "Draft" },
  { value: ARCHIVED_TEMPLATE_STATUS, label: "Archived" },
];

export function templateStatusLabel(status: string): string {
  return AI_TEMPLATE_STATUSES.find((entry) => entry.value === status)?.label ?? status;
}

export interface AvailableTool {
  name: string;
  label: string;
  description: string;
}

export const AVAILABLE_AI_TOOLS: AvailableTool[] = [
  {
    name: "lookup_contact",
    label: "Look up contact",
    description: "Find an existing contact by phone number in the CRM.",
  },
  {
    name: "set_outcome",
    label: "Set outcome",
    description:
      "Record the final outcome of this call (drives workflow onOutcome routing). Must be one of the agent's declared outcomes.",
  },
  {
    name: "transfer_call",
    label: "Transfer call",
    description: "Hand the call off to a human agent or another phone number.",
  },
  {
    name: "lookup_property",
    label: "Look up property",
    description:
      "Find a property in the CRM by address. Returns characteristics and owner/occupancy details — never prices or valuations.",
  },
  {
    name: "save_call_details",
    label: "Save call details",
    description:
      "Record what the caller shares (motivation, timeline, condition, occupancy, decision makers). Saved onto the call record for the team to review.",
  },
  {
    name: "schedule_callback",
    label: "Schedule callback",
    description:
      "Book a follow-up as a task linked to the caller's contact, with the time they asked for.",
  },
  {
    name: "update_contact",
    label: "Update contact",
    description:
      "Correct the caller's own CRM details (name, address, company) when they tell the agent something has changed.",
  },
];

export function isToolEnabled(tools: Record<string, any> | null | undefined, toolName: string): boolean {
  if (!tools) {
    return false;
  }
  const entry = tools[toolName];
  if (entry === true) {
    return true;
  }
  if (entry && typeof entry === "object") {
    return entry.enabled !== false;
  }
  return false;
}

export function toggleAgentTool(
  tools: Record<string, any> | null | undefined,
  toolName: string,
  enabled: boolean,
): Record<string, any> {
  const next = { ...(tools ?? {}) };
  if (enabled) {
    next[toolName] = { enabled: true };
  } else {
    delete next[toolName];
  }
  return next;
}
